import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { ArrowLeft, Calendar, Search, UserCheck, Users, X, CheckCircle2, AlertTriangle, Circle, ChevronDown, ChevronLeft, ChevronRight } from 'lucide-react'
import { Card, Badge, Button, ProgressBar } from '@/components/ui'
import { useAuthStore } from '@/store'
import { useManagementCompanySimple } from '@/hooks/useManagementCompanySimple'
import {
  getPersonalManagerDayRows,
  getPersonalManagerPartners,
  listPersonalManagers,
  setPersonalManagerDayOff,
  setPersonalManagerPartnerDayOverride,
  setPersonalManagerTaskMark,
  unassignPersonalManager,
} from '@/services/personalManagerService'
import type {
  PersonalManagerDayRow,
  PersonalManagerPartnerDayStatus,
  PersonalManagerListItem,
  PersonalManagerPartner,
  PersonalManagerTaskCode,
  PersonalManagerTaskDisplayStatus,
  PersonalManagerTaskStatus,
} from '@/types'
import {
  getPersonalManagerTaskDeadlineLabel,
  getPersonalManagerRowTaskFact,
  getPersonalManagerRowTaskFilledCount,
  getPersonalManagerRowTaskPlan,
  getPersonalManagerRowsSummary,
  getPersonalManagerRowsTaskCounters,
  getPersonalManagerTaskFillProgress,
  getPersonalManagerTaskMarkStatus,
  getPersonalManagerTaskProgress,
  getPersonalManagerTaskScopeRows,
  getPersonalManagerTaskStatus,
  getPersonalManagerTaskStatusForRows,
  getVisiblePersonalManagerTasks,
  isPartnerScopedPersonalManagerTask,
  isPersonalManagerTaskDone,
  todayMskISO,
  type PersonalManagerTaskDefinition,
} from '@/lib/personalManagerTasks'
import { formatPercentage, formatPartnerDisplay } from '@/lib/utils'
import { PersonalManagerRoyaltyCard } from '@/components/personalManager/PersonalManagerRoyaltyCard'

function addDays(iso: string, days: number): string {
  const date = new Date(`${iso}T00:00:00Z`)
  date.setUTCDate(date.getUTCDate() + days)
  return date.toISOString().slice(0, 10)
}

function formatDateTime(value: string | null): string {
  if (!value) return '—'
  return new Intl.DateTimeFormat('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(value))
}

function formatDateTimeInZone(value: string | null, timeZone: string): string {
  if (!value) return '—'
  return new Intl.DateTimeFormat('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    timeZone,
  }).format(new Date(value))
}

function formatStudioAndMskTime(value: string | null, timeZone: string | null): string {
  if (!value) return '—'
  const studioTime = formatDateTimeInZone(value, timeZone || 'Europe/Moscow')
  const mskTime = formatDateTimeInZone(value, 'Europe/Moscow')
  return `${studioTime} местное (${mskTime} МСК)`
}

function formatNumber(value: number | string | null | undefined): string {
  if (value === null || value === undefined || value === '') return '—'
  const parsed = typeof value === 'number'
    ? value
    : Number(String(value).replace(/\s/g, '').replace(',', '.'))
  if (!Number.isFinite(parsed)) return '—'
  return new Intl.NumberFormat('ru-RU', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(parsed)
}

function statusTone(status: PersonalManagerTaskDisplayStatus): 'success' | 'error' | 'warning' | 'neutral' {
  if (status === 'done') return 'success'
  if (status === 'day_off') return 'warning'
  if (status === 'late_done') return 'warning'
  if (status === 'pending') return 'neutral'
  return 'error'
}

function statusText(status: PersonalManagerTaskDisplayStatus): string {
  if (status === 'done') return 'Готово'
  if (status === 'day_off') return 'Выходной'
  if (status === 'late_done') return 'С опозданием'
  if (status === 'pending') return 'Не отмечено'
  return 'Нет'
}

function nonWorkingStatusText(status: PersonalManagerPartnerDayStatus | null): string {
  if (status === 'vacation') return 'Отпуск'
  if (status === 'day_off') return 'Выходной'
  return 'Готово'
}

function getRowsPartnerDayStatus(rows: PersonalManagerDayRow[]): PersonalManagerPartnerDayStatus | null {
  return rows.find(row => row.partner_day_status != null)?.partner_day_status ?? null
}

function managerName(manager: PersonalManagerListItem | null, userId: string) {
  return manager?.manager_name || manager?.tg_username || userId.slice(0, 8)
}

function getRowSummary(row: PersonalManagerDayRow) {
  const plan = getPersonalManagerRowTaskPlan(row.task_date)
  const fact = getPersonalManagerRowTaskFact(row)
  const filled = getPersonalManagerRowTaskFilledCount(row)
  return {
    plan,
    fact,
    filled,
    filledPct: plan > 0 ? (filled / plan) * 100 : 0,
    pct: plan > 0 ? (fact / plan) * 100 : 0,
  }
}

function getFirstDateValue(values: Array<string | null | undefined>): string | null {
  const normalized = values.filter((value): value is string => Boolean(value)).sort()
  return normalized[0] ?? null
}

function getLastDateValue(values: Array<string | null | undefined>): string | null {
  const normalized = values.filter((value): value is string => Boolean(value)).sort()
  return normalized.length > 0 ? normalized[normalized.length - 1] : null
}

function taskSubtitle(rows: PersonalManagerDayRow[], task: Pick<PersonalManagerTaskDefinition, 'field' | 'deadlineTimeMsk'>): string | null {
  const row = rows[0]
  if (!row) return null

  if (task.field === 'app_status') {
    return `Первое сохранение: ${formatStudioAndMskTime(row.app_first_changed_at, row.iana_timezone)} · Последнее: ${formatStudioAndMskTime(row.app_last_changed_at, row.iana_timezone)}`
  }

  if (task.field === 'revenue_status') {
    return `Приложение: ${formatNumber(row.app_revenue)} · CRM: ${formatNumber(row.crm_revenue)} · Δ: ${formatNumber(row.revenue_delta)} · Синк: ${formatDateTime(row.revenue_last_sync_at)}`
  }

  if (task.field === 'partner_report_status') {
    return `Первое изменение: ${formatDateTime(row.partner_report_first_changed_at)} · Последнее: ${formatDateTime(row.partner_report_marked_at)}`
  }

  if (task.field === 'standup_status') {
    return `Первое изменение: ${formatDateTime(getFirstDateValue(rows.map(item => item.standup_first_changed_at)))} · Последнее: ${formatDateTime(getLastDateValue(rows.map(item => item.standup_marked_at)))}`
  }

  return null
}

export function PersonalManagerDetailPage() {
  const { userId = '' } = useParams<{ userId: string }>()
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const { getAccessLevel } = useManagementCompanySimple(user?.id)

  const [manager, setManager] = useState<PersonalManagerListItem | null>(null)
  const [partners, setPartners] = useState<PersonalManagerPartner[]>([])
  const [rows, setRows] = useState<PersonalManagerDayRow[]>([])
  const [selectedDate, setSelectedDate] = useState(todayMskISO)
  const [search, setSearch] = useState('')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [busyKey, setBusyKey] = useState<string | null>(null)
  const [isAdvanced, setIsAdvanced] = useState(false)
  const [busyAssignmentId, setBusyAssignmentId] = useState<number | null>(null)
  const [showAssignments, setShowAssignments] = useState(false)
  const [viewMode, setViewMode] = useState<'blocks' | 'studios'>('blocks')
  const [collapsedTaskBlocks, setCollapsedTaskBlocks] = useState<Record<string, boolean>>({})
  const [collapsedStudios, setCollapsedStudios] = useState<Record<number, boolean>>({})
  const [selectedPartnerNumber, setSelectedPartnerNumber] = useState<string>('')
  const [selectedPartnerDayStatus, setSelectedPartnerDayStatus] = useState<PersonalManagerPartnerDayStatus | null>(null)

  const visibleTasks = useMemo(
    () => getVisiblePersonalManagerTasks(selectedDate),
    [selectedDate]
  )

  const reload = () => {
    if (!userId) return
    setLoading(true)
    Promise.all([
      listPersonalManagers(),
      getPersonalManagerPartners(userId),
      getPersonalManagerDayRows(userId, selectedDate),
    ])
      .then(([list, partnerRows, dayRows]) => {
        setManager(list.find(item => item.manager_user_id === userId) ?? null)
        setPartners(partnerRows)
        setRows(dayRows)
        setError(null)
      })
      .catch(err => setError(err instanceof Error ? err.message : String(err)))
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    reload()
  }, [userId, selectedDate])

  useEffect(() => {
    let cancelled = false
    if (!user?.id) return

    getAccessLevel().then(level => {
      if (!cancelled) setIsAdvanced(level === 'advanced')
    })

    return () => { cancelled = true }
  }, [user?.id, getAccessLevel])

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return rows

    return rows.filter(row =>
      formatPartnerDisplay(row.partner_number, row.persons_fio, row.cities_text).toLowerCase().includes(q) ||
      String(row.partner_number).includes(q) ||
      (row.studio_number ?? '').toLowerCase().includes(q) ||
      (row.city_name ?? '').toLowerCase().includes(q)
    )
  }, [rows, search])

  const totals = useMemo(() => {
    if (rows.length === 0) return { plan: 0, fact: 0, pct: null as number | null }
    const summary = getPersonalManagerRowsSummary(rows, selectedDate)
    return { ...summary, pct: summary.plan > 0 ? summary.pct : null }
  }, [rows, selectedDate])

  const canTryEdit = user?.id === userId || isAdvanced

  const toggleDayOff = async () => {
    const currentlyOff = rows.some(row => row.day_off)
    setBusyKey('day-off')
    try {
      await setPersonalManagerDayOff({
        managerUserId: userId,
        taskDate: selectedDate,
        isDayOff: !currentlyOff,
      })
      await getPersonalManagerDayRows(userId, selectedDate).then(setRows)
      setError(null)
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setBusyKey(null)
    }
  }

  const applyPartnerDayStatus = async (overrideType: PersonalManagerPartnerDayStatus | null) => {
    if (!selectedPartnerNumber) return

    setBusyKey(`partner-day-status:${overrideType ?? 'clear'}`)
    try {
      await setPersonalManagerPartnerDayOverride({
        taskDate: selectedDate,
        partnerNumber: Number(selectedPartnerNumber),
        overrideType,
      })
      await getPersonalManagerDayRows(userId, selectedDate).then(setRows)
      setError(null)
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setBusyKey(null)
    }
  }

  const handleUnassign = async (partner: PersonalManagerPartner) => {
    const ok = confirm(`Снять франчайзи ${partner.partner_number} с этого ЛМ сегодняшней датой?`)
    if (!ok) return

    setBusyAssignmentId(partner.assignment_id)
    setError(null)
    try {
      await unassignPersonalManager({ assignmentId: partner.assignment_id })
      reload()
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setBusyAssignmentId(null)
    }
  }

  const setTaskForAllStudios = async (
    task: typeof visibleTasks[number],
    status: PersonalManagerTaskStatus
  ) => {
    if (!task.editable || task.key === 'app_filled') return

    const key = `task:${task.key}:${status}`
    setBusyKey(key)
    try {
      const targetRows = getPersonalManagerTaskScopeRows(
        filteredRows.filter(row => !row.day_off),
        task
      ).map(scopeRow => scopeRow.representativeRow)

      for (const row of targetRows) {
        await setPersonalManagerTaskMark({
          taskDate: selectedDate,
          studioInternalId: row.studio_internal_id,
          taskCode: task.key as PersonalManagerTaskCode,
          status,
        })
      }

      await getPersonalManagerDayRows(userId, selectedDate).then(setRows)
      setError(null)
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setBusyKey(null)
    }
  }

  const setTaskForStudio = async (
    row: PersonalManagerDayRow,
    task: typeof visibleTasks[number],
    status: PersonalManagerTaskStatus
  ) => {
    if (!task.editable || task.key === 'app_filled') return

    const key = `${row.studio_internal_id}:${task.key}:${status}`
    setBusyKey(key)
    try {
      await setPersonalManagerTaskMark({
        taskDate: selectedDate,
        studioInternalId: row.studio_internal_id,
        taskCode: task.key as PersonalManagerTaskCode,
        status,
      })
      await getPersonalManagerDayRows(userId, selectedDate).then(setRows)
      setError(null)
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setBusyKey(null)
    }
  }

  const dayOff = rows.some(row => row.day_off)
  const taskCounters = useMemo(
    () => getPersonalManagerRowsTaskCounters(rows, selectedDate),
    [rows, selectedDate]
  )
  const tasksInWork = taskCounters.pending + taskCounters.problem

  const studioRows = useMemo(() => {
    return [...filteredRows].sort((a, b) => {
      const cityDiff = String(a.city_name ?? '').localeCompare(String(b.city_name ?? ''), 'ru', { sensitivity: 'base' })
      if (cityDiff !== 0) return cityDiff
      const studioDiff = String(a.studio_number ?? a.studio_internal_id).localeCompare(
        String(b.studio_number ?? b.studio_internal_id),
        'ru',
        { numeric: true, sensitivity: 'base' }
      )
      if (studioDiff !== 0) return studioDiff
      return a.partner_number - b.partner_number
    })
  }, [filteredRows])

  const partnerOptions = useMemo(() => {
    const map = new Map<number, { label: string; status: PersonalManagerPartnerDayStatus | null }>()

    for (const row of studioRows) {
      if (!map.has(row.partner_number)) {
        map.set(row.partner_number, {
          label: formatPartnerDisplay(row.partner_number, row.persons_fio, row.cities_text),
          status: row.partner_day_status,
        })
      }
    }

    return [...map.entries()]
      .map(([partnerNumber, value]) => ({
        partnerNumber,
        label: value.label,
        status: value.status,
      }))
      .sort((a, b) => a.label.localeCompare(b.label, 'ru', { sensitivity: 'base' }))
  }, [studioRows])

  useEffect(() => {
    if (!selectedPartnerNumber && partnerOptions.length === 1) {
      setSelectedPartnerNumber(String(partnerOptions[0].partnerNumber))
    }
  }, [partnerOptions, selectedPartnerNumber])

  useEffect(() => {
    if (!selectedPartnerNumber) return
    const exists = partnerOptions.some(option => String(option.partnerNumber) === selectedPartnerNumber)
    if (!exists) {
      setSelectedPartnerNumber('')
      setSelectedPartnerDayStatus(null)
    }
  }, [partnerOptions, selectedPartnerNumber])

  useEffect(() => {
    if (!selectedPartnerNumber) {
      setSelectedPartnerDayStatus(null)
      return
    }

    const selected = partnerOptions.find(option => String(option.partnerNumber) === selectedPartnerNumber)
    setSelectedPartnerDayStatus(selected?.status ?? null)
  }, [partnerOptions, selectedPartnerNumber])

  useEffect(() => {
    setCollapsedTaskBlocks(prev => {
      const next = { ...prev }
      for (const task of visibleTasks) {
        if (!(task.key in next)) next[task.key] = true
      }
      return next
    })
  }, [visibleTasks])

  useEffect(() => {
    setCollapsedStudios(prev => {
      const next = { ...prev }
      for (const row of studioRows) {
        if (!(row.studio_internal_id in next)) next[row.studio_internal_id] = true
      }
      return next
    })
  }, [studioRows])

  return (
    <div className="space-y-4 pb-24">
      <div className="flex items-center justify-between gap-2">
        <Button variant="outline" size="sm" onClick={() => navigate('/management/personal-managers')}>
          <ArrowLeft size={16} className="mr-1" />
          Назад
        </Button>
        <Badge text="ЛМ" variant="success" className="text-xs" />
      </div>

      <Card className="p-4">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div className="inline-flex rounded-2xl border border-border bg-muted/30 p-1">
            <button
              onClick={() => setViewMode('blocks')}
              className={`rounded-xl px-4 py-2 text-sm font-medium transition ${viewMode === 'blocks' ? 'bg-background text-amber-600 shadow-sm ring-1 ring-amber-200' : 'text-muted-foreground'}`}
            >
              По блокам
            </button>
            <button
              onClick={() => setViewMode('studios')}
              className={`rounded-xl px-4 py-2 text-sm font-medium transition ${viewMode === 'studios' ? 'bg-background text-amber-600 shadow-sm ring-1 ring-amber-200' : 'text-muted-foreground'}`}
            >
              По студиям
            </button>
          </div>

          <div className="flex items-center justify-between gap-2 rounded-2xl border border-border bg-background px-3 py-2 lg:min-w-[300px]">
            <button onClick={() => setSelectedDate(addDays(selectedDate, -1))} className="rounded-lg p-1 text-muted-foreground hover:bg-muted hover:text-foreground">
              <ChevronLeft size={18} />
            </button>
            <div className="flex items-center gap-2 text-sm font-medium">
              <Calendar size={16} className="text-muted-foreground" />
              {new Intl.DateTimeFormat('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' }).format(new Date(`${selectedDate}T00:00:00`))}
            </div>
            <button onClick={() => setSelectedDate(addDays(selectedDate, 1))} className="rounded-lg p-1 text-muted-foreground hover:bg-muted hover:text-foreground">
              <ChevronRight size={18} />
            </button>
          </div>

          <Button
            variant={dayOff ? 'secondary' : 'outline'}
            size="sm"
            disabled={!canTryEdit || busyKey === 'day-off'}
            onClick={toggleDayOff}
          >
            {dayOff ? 'Снять выходной' : 'Выходной'}
          </Button>
        </div>
      </Card>

      <Card className="p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <UserCheck size={18} className="text-primary" />
              <h1 className="text-lg font-semibold truncate">{managerName(manager, userId)}</h1>
            </div>
            <div className="mt-1 text-xs text-muted-foreground">
              {manager?.tg_username ? `@${manager.tg_username}` : 'без Telegram'} · {partners.length} франч. · {manager?.studios_count ?? rows.length} студ.
            </div>
          </div>
          <div className="text-right">
            <div className="text-lg font-semibold">{totals.pct == null ? '—' : formatPercentage(totals.pct)}</div>
            <div className="text-xs text-muted-foreground">{totals.fact} / {totals.plan}</div>
          </div>
        </div>
        <ProgressBar value={totals.pct ?? 0} className="mt-3" />
      </Card>

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Card className="p-4">
          <div className="text-xs text-muted-foreground">Студий всего</div>
          <div className="mt-1 text-2xl font-semibold">{rows.length}</div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-2 text-xs text-muted-foreground"><CheckCircle2 size={14} className="text-success" /> Закрыто</div>
          <div className="mt-1 text-2xl font-semibold text-success">{taskCounters.done}</div>
          <div className="text-xs text-muted-foreground">{totals.pct == null ? '—' : formatPercentage(totals.pct)}</div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-2 text-xs text-muted-foreground"><AlertTriangle size={14} className="text-error" /> Проблемные</div>
          <div className="mt-1 text-2xl font-semibold text-error">{taskCounters.problem}</div>
          <div className="text-xs text-muted-foreground">из {taskCounters.total}</div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-2 text-xs text-muted-foreground"><Circle size={14} className="text-muted-foreground" /> В работе</div>
          <div className="mt-1 text-2xl font-semibold">{tasksInWork}</div>
          <div className="text-xs text-muted-foreground">не закрыто</div>
        </Card>
      </div>

      {userId && <PersonalManagerRoyaltyCard managerUserId={userId} />}

      {isAdvanced && (
        <Card>
          <div className="flex items-center justify-between gap-2">
            <button
              className="flex items-center gap-2 text-sm font-semibold"
              onClick={() => setShowAssignments(value => !value)}
            >
              <Users size={16} className="text-primary" />
              Назначенные франчайзи ({partners.length})
            </button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/management/personal-managers-coverage')}
            >
              Управление назначениями
            </Button>
          </div>
          {showAssignments && (
            <div className="mt-3 divide-y divide-border">
              {partners.length === 0 && (
                <div className="py-2 text-xs text-muted-foreground">
                  Франчайзи не назначены. Используйте «Управление назначениями».
                </div>
              )}
              {partners.map(partner => (
                <div key={partner.assignment_id} className="flex items-center justify-between gap-3 py-2">
                  <div className="min-w-0">
                    <div className="truncate text-sm font-medium">
                      {formatPartnerDisplay(partner.partner_number, partner.persons_fio, partner.cities_text)}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {partner.studio_count} студ. · с {partner.assigned_from}
                      {partner.assigned_to ? ` · снятие ${partner.assigned_to}` : ''}
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={busyAssignmentId === partner.assignment_id}
                    onClick={() => handleUnassign(partner)}
                  >
                    <X size={14} className="mr-1" /> Снять
                  </Button>
                </div>
              ))}
            </div>
          )}
        </Card>
      )}

      <Card>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={16} />
          <input
            value={search}
            onChange={event => setSearch(event.target.value)}
            placeholder="Поиск по франчайзи, студии или городу"
            className="w-full rounded-lg border border-input bg-background py-2 pl-9 pr-3 text-sm"
          />
        </div>
      </Card>

      <Card className="p-4">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-end">
          <div className="min-w-0 flex-1">
            <div className="mb-1 text-sm font-semibold">Выходной / отпуск у франчайзи</div>
            <div className="text-xs text-muted-foreground">
              Выходной или отпуск у франчайзи автоматически засчитывает весь его чек-лист за выбранную дату.
            </div>
          </div>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-[minmax(260px,1fr)_auto_auto_auto] sm:items-end lg:min-w-[760px]">
            <label className="text-xs text-muted-foreground">
              Франчайзи
              <select
                value={selectedPartnerNumber}
                onChange={event => setSelectedPartnerNumber(event.target.value)}
                className="mt-1 w-full rounded-lg border border-input bg-background px-3 py-2 text-sm text-foreground"
              >
                <option value="">Выберите франчайзи</option>
                {partnerOptions.map(option => (
                  <option key={option.partnerNumber} value={option.partnerNumber}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>

            <Button
              size="sm"
              variant={selectedPartnerDayStatus === 'day_off' ? 'secondary' : 'outline'}
              disabled={!canTryEdit || !selectedPartnerNumber || busyKey === 'partner-day-status:day_off'}
              onClick={() => applyPartnerDayStatus('day_off')}
            >
              Выходной
            </Button>

            <Button
              size="sm"
              variant={selectedPartnerDayStatus === 'vacation' ? 'secondary' : 'outline'}
              disabled={!canTryEdit || !selectedPartnerNumber || busyKey === 'partner-day-status:vacation'}
              onClick={() => applyPartnerDayStatus('vacation')}
            >
              Отпуск
            </Button>

            <Button
              size="sm"
              variant="outline"
              disabled={!canTryEdit || !selectedPartnerNumber || !selectedPartnerDayStatus || busyKey === 'partner-day-status:clear'}
              onClick={() => applyPartnerDayStatus(null)}
            >
              Снять
            </Button>
          </div>
        </div>
      </Card>

      {loading && <Card><div className="text-sm text-muted-foreground">Загрузка...</div></Card>}
      {error && <Card><div className="text-sm text-destructive">{error}</div></Card>}

      {!loading && filteredRows.length === 0 && (
        <Card><div className="text-sm text-muted-foreground">По выбранной дате строк нет.</div></Card>
      )}

      {viewMode === 'blocks' ? (
        <div className="space-y-3">
          {visibleTasks.map(task => {
            const scopeRows = getPersonalManagerTaskScopeRows(studioRows, task)
            const { done, total } = getPersonalManagerTaskProgress(studioRows, task)
            const { filled } = getPersonalManagerTaskFillProgress(studioRows, task)
            const isFilled = filled === total
            const isCollapsed = collapsedTaskBlocks[task.key] ?? false
            const progressUnit = isPartnerScopedPersonalManagerTask(task.key)
              ? (task.key === 'content_posted' ? 'франчайзи / городов' : 'франчайзи')
              : 'студий'

            return (
              <Card key={task.key} className="overflow-hidden rounded-3xl border border-border/80 shadow-sm">
                <div className="space-y-3 p-4">
                  <button
                    type="button"
                    onClick={() => setCollapsedTaskBlocks(prev => ({ ...prev, [task.key]: !isCollapsed }))}
                    className="flex w-full flex-col gap-3 text-left lg:flex-row lg:items-center lg:justify-between"
                  >
                    <div>
                      <div className="flex items-center gap-2">
                        <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform ${isCollapsed ? '-rotate-90' : ''}`} />
                        <div className="text-lg font-semibold">
                          {task.label} <span>· до {getPersonalManagerTaskDeadlineLabel(task)}</span>
                        </div>
                      </div>
                      <div className="mt-2 flex flex-wrap items-center gap-2">
                        <span className="inline-flex rounded-full bg-amber-50 px-2.5 py-1 text-xs font-medium text-amber-700 ring-1 ring-amber-100">
                          Засчитано: {done} / {total} {progressUnit}
                        </span>
                        <span
                          className={`inline-flex rounded-full px-2.5 py-1 text-xs font-medium ring-1 ${
                            isFilled
                              ? 'bg-emerald-50 text-emerald-700 ring-emerald-100'
                              : 'bg-rose-50 text-rose-700 ring-rose-100'
                          }`}
                        >
                          Фактически заполнено: {filled} / {total}
                        </span>
                      </div>
                    </div>
                  </button>

                  {!isCollapsed && task.editable && task.key !== 'app_filled' && (
                    <div className="flex flex-wrap gap-2 rounded-2xl border border-amber-200 bg-amber-50 px-3 py-2">
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={!canTryEdit || busyKey === `task:${task.key}:done`}
                        onClick={() => setTaskForAllStudios(task, 'done')}
                      >
                        Проставить всем Да
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={!canTryEdit || busyKey === `task:${task.key}:not_done`}
                        onClick={() => setTaskForAllStudios(task, 'not_done')}
                      >
                        Проставить всем Нет
                      </Button>
                    </div>
                  )}

                  {!isCollapsed && (
                  <div className="space-y-2">
                    {scopeRows.map(scopeRow => {
                      const targetRow = scopeRow.representativeRow
                      const status = getPersonalManagerTaskStatusForRows(scopeRow.rows, task)
                      const markStatus = getPersonalManagerTaskMarkStatus(targetRow, task)
                      const partnerDayStatus = getRowsPartnerDayStatus(scopeRow.rows)
                      const scopeSummary = getPersonalManagerRowsSummary(scopeRow.rows, selectedDate)
                      const title = isPartnerScopedPersonalManagerTask(task.key)
                        ? (scopeRow.splitByCity
                          ? (scopeRow.cityName ?? 'Без города')
                          : formatPartnerDisplay(targetRow.partner_number, targetRow.persons_fio, targetRow.cities_text))
                        : `${targetRow.studio_number ? `${targetRow.studio_number} · ` : ''}${targetRow.city_name ?? 'Студия'}`
                      const subtitle = isPartnerScopedPersonalManagerTask(task.key)
                        ? (scopeRow.splitByCity
                          ? formatPartnerDisplay(targetRow.partner_number, targetRow.persons_fio, targetRow.cities_text)
                          : `${scopeRow.rows.length} студ.`)
                        : formatPartnerDisplay(targetRow.partner_number, targetRow.persons_fio, targetRow.cities_text)
                      const subtitleText = taskSubtitle(scopeRow.rows, task)
                      const allRowsDayOff = scopeRow.rows.every(row => row.day_off || row.partner_day_status != null)

                      return (
                        <div key={`${task.key}:${scopeRow.key}`} className="rounded-2xl border border-border bg-background p-3">
                          <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                            <div className="min-w-0">
                              <div className="flex items-center gap-2">
                                <div className="truncate text-sm font-semibold">{title}</div>
                                <Badge
                                  text={status === 'day_off' ? nonWorkingStatusText(partnerDayStatus) : statusText(status)}
                                  variant={statusTone(status)}
                                  className="text-xs"
                                />
                              </div>
                              {subtitle && (
                                <div className="truncate text-xs text-muted-foreground">
                                  {subtitle}
                                </div>
                              )}
                              <div className="mt-1 text-xs text-muted-foreground">
                                День: {scopeSummary.fact} / {scopeSummary.plan}
                              </div>
                              {subtitleText && (
                                <div className="mt-1 text-xs text-muted-foreground">
                                  {subtitleText}
                                </div>
                              )}
                            </div>

                            <div className="flex flex-wrap items-center gap-2">
                              {task.editable && task.key !== 'app_filled' && !allRowsDayOff && (
                                <div className="flex items-center gap-1">
                                  <Button
                                    size="sm"
                                    variant={markStatus === 'done' ? 'secondary' : 'outline'}
                                    disabled={!canTryEdit || busyKey === `${targetRow.studio_internal_id}:${task.key}:done`}
                                    onClick={() => setTaskForStudio(targetRow, task, 'done')}
                                  >
                                    Да
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant={markStatus === 'not_done' ? 'secondary' : 'outline'}
                                    disabled={!canTryEdit || busyKey === `${targetRow.studio_internal_id}:${task.key}:not_done`}
                                    onClick={() => setTaskForStudio(targetRow, task, 'not_done')}
                                  >
                                    Нет
                                  </Button>
                                  {task.key === 'standup' && (
                                    <Button
                                      size="sm"
                                      variant={markStatus === 'day_off' ? 'secondary' : 'outline'}
                                      disabled={!canTryEdit || busyKey === `${targetRow.studio_internal_id}:${task.key}:day_off`}
                                      onClick={() => setTaskForStudio(targetRow, task, 'day_off')}
                                    >
                                      Н/П
                                    </Button>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                  )}
                </div>
              </Card>
            )
          })}
        </div>
      ) : (
        <div className="space-y-3">
          {studioRows.map(row => {
            const studioPlan = getPersonalManagerRowTaskPlan(row.task_date)
            const studioFact = getPersonalManagerRowTaskFact(row)
            const studioFilled = getPersonalManagerRowTaskFilledCount(row)
            const studioPct = studioPlan > 0 ? (studioFact / studioPlan) * 100 : null
            const studioFilledAll = studioFilled === studioPlan
            const isCollapsed = collapsedStudios[row.studio_internal_id] ?? false

            return (
              <Card key={row.studio_internal_id} className="overflow-hidden rounded-3xl border border-border/80 shadow-sm">
                <div className="p-4">
                  <button
                    type="button"
                    onClick={() => setCollapsedStudios(prev => ({ ...prev, [row.studio_internal_id]: !isCollapsed }))}
                    className="flex w-full items-start gap-3 text-left"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 truncate text-base font-semibold">
                        <ChevronDown className={`h-4 w-4 shrink-0 text-muted-foreground transition-transform ${isCollapsed ? '-rotate-90' : ''}`} />
                        {row.studio_number ? `${row.studio_number} · ` : ''}{row.city_name ?? 'Студия'}
                      </div>
                      <div className="truncate text-sm text-muted-foreground">
                        {formatPartnerDisplay(row.partner_number, row.persons_fio, row.cities_text)}
                      </div>
                      <div className="mt-2 flex flex-wrap gap-2">
                        <span className="inline-flex rounded-full bg-amber-50 px-2.5 py-1 text-xs font-medium text-amber-700 ring-1 ring-amber-100">
                          Засчитано: {studioFact} / {studioPlan}
                        </span>
                        <span
                          className={`inline-flex rounded-full px-2.5 py-1 text-xs font-medium ring-1 ${
                            studioFilledAll
                              ? 'bg-emerald-50 text-emerald-700 ring-emerald-100'
                              : 'bg-rose-50 text-rose-700 ring-rose-100'
                          }`}
                        >
                          Фактически заполнено: {studioFilled} / {studioPlan}
                        </span>
                      </div>
                    </div>
                    <div className="flex shrink-0 flex-wrap justify-end gap-2">
                      <ChevronDown className={`mt-1 h-4 w-4 shrink-0 text-muted-foreground transition-transform ${isCollapsed ? '-rotate-90' : ''}`} />
                    </div>
                  </button>

                  {!isCollapsed && (
                  <div className="mt-3 space-y-2 rounded-2xl border border-border bg-background p-3">
                    <div className="text-sm font-medium text-muted-foreground">{visibleTasks.length} проверок на сегодня</div>
                    {visibleTasks.map(task => {
                      const status = getPersonalManagerTaskStatus(row, task)
                      const markStatus = getPersonalManagerTaskMarkStatus(row, task)
                      const subtitle = taskSubtitle([row], task)
                      const taskCode = task.key === 'app_filled' ? null : task.key
                      const canEditTask = Boolean(taskCode) && task.editable && canTryEdit && !row.day_off && row.partner_day_status == null

                      return (
                        <div key={task.field} className="rounded-xl border border-border px-3 py-2">
                          <div className="flex flex-col gap-2 lg:flex-row lg:items-center lg:justify-between">
                            <div className="min-w-0">
                              <div className="text-sm font-medium">
                                {task.label} <span>· до {getPersonalManagerTaskDeadlineLabel(task)}</span>
                              </div>
                              {subtitle && (
                                <div className="mt-1 text-xs text-muted-foreground">
                                  {subtitle}
                                </div>
                              )}
                            </div>
                            <div className="flex shrink-0 items-center gap-2">
                              <Badge
                                text={status === 'day_off' ? nonWorkingStatusText(row.partner_day_status) : statusText(status)}
                                variant={statusTone(status)}
                                className="text-xs"
                              />
                              {canEditTask && taskCode && (
                                <div className="flex items-center gap-1">
                                  <Button
                                    size="sm"
                                    variant={markStatus === 'done' ? 'secondary' : 'outline'}
                                    disabled={!canTryEdit || busyKey === `${row.studio_internal_id}:${task.key}:done`}
                                    onClick={() => setTaskForStudio(row, task, 'done')}
                                  >
                                    Да
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant={markStatus === 'not_done' ? 'secondary' : 'outline'}
                                    disabled={!canTryEdit || busyKey === `${row.studio_internal_id}:${task.key}:not_done`}
                                    onClick={() => setTaskForStudio(row, task, 'not_done')}
                                  >
                                    Нет
                                  </Button>
                                  {taskCode === 'standup' && (
                                    <Button
                                      size="sm"
                                      variant={markStatus === 'day_off' ? 'secondary' : 'outline'}
                                      disabled={!canTryEdit || busyKey === `${row.studio_internal_id}:${task.key}:day_off`}
                                      onClick={() => setTaskForStudio(row, task, 'day_off')}
                                    >
                                      Н/П
                                    </Button>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                  )}
                </div>
              </Card>
            )
          })}
        </div>
      )}
    </div>
  )
}
